import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BanknotesIcon,
  ChartBarIcon,
  CheckIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  TicketIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import StatusBadge from "./StatusBadge";

const money = (value) =>
  Number.isFinite(Number(value)) ? `${Number(value)} Ks` : "—";

const moneyPound = (value) =>
  Number.isFinite(Number(value)) ? `€${Number(value).toFixed(2)}` : "—";

const stateStyle = {
  complete: "bg-emerald-500 text-white",
  current: "bg-customAccentBlue text-white",
  failed: "bg-red-500 text-white",
  upcoming:
    "bg-slate-200 text-slate-600 dark:bg-customSurfaceMuted dark:text-customMuted",
};

const StageIcon = ({ stage }) => {
  if (stage.state === "failed")
    return <XMarkIcon className="h-4 w-4 stroke-[3]" />;
  if (stage.state === "complete")
    return <CheckIcon className="h-4 w-4 stroke-[3]" />;
  if (stage.key === "recalculated")
    return <ArrowPathIcon className="h-4 w-4" />;
  return <ClockIcon className="h-4 w-4" />;
};

const Metric = ({ icon: Icon, label, value, positive, negative }) => (
  <div
    className={`flex min-h-[62px] items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-customBorder ${positive ? "bg-emerald-50/70 dark:bg-emerald-950/20" : negative ? "bg-red-50/70 dark:bg-red-950/20" : "bg-slate-50/70 dark:bg-custom_dark_primary"}`}
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-customAccentBlue dark:bg-blue-950/60">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="text-[11px] text-slate-500 dark:text-customMuted">
        {label}
      </p>
      <p
        className={`text-sm font-bold ${positive ? "text-emerald-700 dark:text-emerald-400" : negative ? "text-red-700 dark:text-red-400" : "text-customInk dark:text-white"}`}
      >
        {value}
      </p>
    </div>
  </div>
);

export default function VoucherOverview({ voucher, viewDetails }) {
  const details = voucher.details || {};
  const lifecycle = details.lifecycle || [];
  //console.log("life cycle", lifecycle);
  const netResult = Number(details.netResult || 0);

  // const exportVoucher = () => {
  //   const file = new Blob([JSON.stringify(voucher, null, 2)], {
  //     type: "application/json",
  //   });
  //   const url = URL.createObjectURL(file);
  //   const anchor = document.createElement("a");
  //   anchor.href = url;
  //   anchor.download = `${voucher.id}.json`;
  //   anchor.click();
  //   URL.revokeObjectURL(url);
  //   toast.success(`${voucher.id} exported`);
  // };

  return (
    <aside className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-customBorder dark:bg-custom_dark_primary2">
      <header className="flex items-start gap-3 border-b border-slate-100 p-4 dark:border-customBorder">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue/20 text-customAccentBlue dark:bg-blue/60">
          <TicketIcon className="h-6 w-6 dark:text-customMuted" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-slate-950 dark:text-white">
              {voucher.posting}
            </h2>
            <StatusBadge status={voucher.status} />
          </div>
          <p className="mt-1 text-xs text-customSurfaceMuted dark:text-customMuted">
            {/* Posting No: {voucher.posting} <span className="px-1">|</span>{" "} */}
            Bet Placed: {details.placedAt || "—"}
          </p>
          <p className="mt-1 text-xs text-customSurfaceMuted dark:text-customMuted">
            User: {voucher.user} <span className="px-1">|</span> Source:{" "}
            {voucher.source} <span className="px-1">|</span> Type:{" "}
            {voucher.type} ({details.legs || 1})
          </p>
        </div>
      </header>
      <div className="px-4 pb-4 pt-5">
        {lifecycle.length > 0 && (
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${lifecycle.length}, minmax(0, 1fr))`,
            }}
          >
            {lifecycle.map((stage, index) => (
              <div key={stage.key} className="relative text-center">
                {index < lifecycle.length - 1 && (
                  <span
                    className={`absolute left-1/2 top-3 h-0.5 w-full ${stage.state === "complete" ? "bg-emerald-500" : stage.state === "failed" ? "bg-red-300" : "bg-slate-200 dark:bg-customBorder"}`}
                  />
                )}
                <span
                  className={`relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full ${stateStyle[stage.state] || stateStyle.upcoming}`}
                >
                  <StageIcon stage={stage} />
                </span>
                <p className="mx-auto mt-2 min-h-[30px] max-w-[76px] text-[10px] font-bold leading-tight text-customInk dark:text-customHeading">
                  { stage.label === "Ledger Posted" ? "Finished" : stage.label }
                </p>
                <p
                  className={`text-[9px] ${stage.state === "current" ? "font-semibold text-customAccentBlue" : stage.state === "failed" ? "font-semibold text-red-500" : "text-customSecondaryText dark:text-customMuted"}`}
                >
                  {stage.at || "Awaiting"}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-3 mt-5 border-b border-slate-200 dark:border-customBorder">
          <span className="inline-block border-b-2 border-customAccentBlue px-2 pb-2 text-xs font-bold text-customAccentBlue">
            Overview
          </span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          <Metric
            icon={BanknotesIcon}
            label="Stake"
            value={money(details.stake)}
          />
          <Metric
            icon={ChartBarIcon}
            label="Gross Return"
            value={money(details.grossReturn)}
          />
          <Metric
            icon={ChartBarIcon}
            label="Proft/Loss"
            value={voucher.status === "Pending" ? money(0) : `${netResult > 0 ? "+" : ""}${money(netResult)}`}
            positive={netResult > 0}
            negative={netResult < 0}
          />
          <Metric
            icon={DocumentTextIcon}
            label="Commission"
            value={money(details.commission)}
          />
          <Metric
            icon={BanknotesIcon}
            label="Payout"
            value={money(details.payout)}
          />
          <Metric
            icon={DevicePhoneMobileIcon}
            label="Source"
            value={details.market || voucher.source}
          />
        </div>
        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3 dark:border-blue-900/50 dark:bg-blue-950/20">
          <p className="text-xs font-bold text-customInk dark:text-customHeading">
            Voucher summary
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-slate-500">Bet type</span>
              <p className="mt-1 font-semibold">{voucher.type}</p>
            </div>
            <div>
              <span className="text-slate-500">Selections</span>
              <p className="mt-1 font-semibold">{details.legs || 1}</p>
            </div>
            <div>
              <span className="text-slate-500">Condition</span>
              <p
                className={`mt-1 font-semibold ${details.outcome === "Won" ? "text-emerald-700" : details.outcome === "Rejected" || details.outcome === "Refunded" || details.outcome === "Lose" ? "text-red-600" : details.outcome === "Pending" ? "text-amber-600" : "text-slate-700"}`}
              >
                {details.outcome || voucher.status}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() =>
              viewDetails(voucher)
            }
            className="flex h-10 items-center justify-center gap-2 rounded-lg border border-customAccentBlue text-xs font-bold text-customAccentBlue hover:bg-blue/20 dark:hover:bg-blue/10"
          >
            <DocumentTextIcon className="h-4 w-4" /> View Voucher
          </button>
          <button
            //onClick={exportVoucher}
            className="flex h-10 items-center justify-center gap-2 rounded-lg border border-customFontRed text-xs font-bold text-customFontRed hover:bg-customFontRed/20 dark:hover:bg-customFontRed/10"
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> Reject Voucher
          </button>
        </div>
      </div>
    </aside>
  );
}

